import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaFileExcel, FaPlus, FaPrint, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import InputDateTime from "../../../../components/input/input-datetime.jsx";
import AssetActivesService from "../../../../services/web/asset-actives-service.js";
import projectUtils from "../../../../utils/project-utils.js";

const statusOpt = [
    {value: "active", label: "Ativo"},
    {value: "decrease", label: "Baixado"}
]

const typeOpt = [
    {value: "electronic", label: "Eletrônico"},
    {value: "furniture", label: "Mobiliário"}
]

const initialData = {
    description: "",
    asset_number: "",
    serial_number: "",
    place: "",
    brand: "",
    model: "",
    status: "",
    type: "",
    warranty: "",
    warrantyEnd: "",
    supplier: ""
}
const AssetActivesList = () => {
    const navigate = navigateHook();
    const [state, setState] = useState(initialData)
    const [filter, setFilter] = useState([])


    const gridRef = useRef(null);
    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    const { data: asset, isLoading } = useQuery({
        queryKey: ['assetActives', filter],
        queryFn: () => AssetActivesService.list(filter).then(({ data }) => data.data ?? [])
    });

    const columnDefs = [
        { headerName: "Descrição", field: "description", flex: 1, comparator: aggridUtils.nullLastComparator},
        { headerName: "Patrimônio", field: "asset_number", flex: 1, comparator: aggridUtils.nullLastComparator},
        { headerName: "Série", field: "serial_number", flex: 1, comparator: aggridUtils.nullLastComparator},
        { headerName: "Local", field: "place", flex: 1, comparator: aggridUtils.nullLastComparator},
        { headerName: "Marca", field: "brand", flex: 1, comparator: aggridUtils.nullLastComparator},
        { headerName: "Modelo", field: "model", flex: 1, comparator: aggridUtils.nullLastComparator},
        { headerName: "Status", field: "status", flex: 1, comparator: aggridUtils.nullLastComparator},
        { headerName: "Tipo", field: "type", flex: 1, comparator: aggridUtils.nullLastComparator},
        { headerName: "Fornecedor", field: "supplier", flex: 1, comparator: aggridUtils.nullLastComparator},
        { headerName: "Garantia", field: "warranty", flex: 1, comparator: aggridUtils.nullLastComparator},
    ];
    const onChange = ({ target: { id, value } }) => {
        setState(prev => ({ ...prev, [id]: value }));
    };
    const handleInputs = (id, value) => setState(prev => ({...prev, [id]: value}));

    const handleDate = useCallback((value, field) => {
        setState(prev => ({
            ...prev,
            [field]: value || null
        }));
    }, []);

    const apply = useCallback(() => {
        gridRef.current?.api?.showLoadingOverlay();

        setFilter({
            description: state.description,
            asset_number: state.asset_number,
            serial_number: state.serial_number,
            place: state.place,
            brand: state.brand,
            model: state.model,
            status: state.status,
            type: state.type,
            warranty: projectUtils.formatDate(state.warranty, "DD/MM/YYYY"),
            warrantyEnd: projectUtils.formatDate(state.warrantyEnd, "DD/MM/YYYY"),
            supplier: state.supplier
        });
    }, [state]);

    const clearFilter = useCallback(() => {
        setState(initialData);
        setFilter([]);
    }, []);

    const onCellClicked = ({ data }) => {
        const isBlank = false;
        const urlDev = `/asset-actives/form/${data.id}`;
        navigate(urlDev, isBlank);
    };

    const newAsset = () => {
        const isBlank = false;
        const urlDev = `/asset-actives/form`;
        navigate(urlDev, isBlank);
    };

    const exportData = () => {
        const csvMaker = (data) => Object.values(data).join(';');

        const array = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort(({data}) => {
            const cloneArray =  {
                "Descrição": data.description,
                "Patrimônio": data.asset_number,
                "Série": data.serial_number,
                "Local": data.place,
                "Marca": data.brand,
                "Modelo": data.model,
                "Status": data.status,
                "Tipo": data.type,
                "Fornecedor": data.supplier,
                "Garantia": data.warranty
            };


            if (!data.total) delete cloneArray.Total;
            array.push(cloneArray);
        });

        projectUtils.exportCsvData(csvMaker, array)
    }

    const openPrint = () => {
        localStorage.removeItem("filter_asset_active")
        localStorage.removeItem("search_without_filter")

        let hasAnyFilter = false;
        for (const [key, value] of Object.entries(filter)) {
            let hasFilterValue;

            if (key !== "warranty" || key !== "warrantyEnd") {
                hasFilterValue = value.length > 0;
            } else {
                hasFilterValue = !!value;
            }

            if (hasFilterValue) {
                hasAnyFilter = true;
                break;
            }
        }

        if (hasAnyFilter) {
            localStorage.setItem("filter_asset_active", JSON.stringify(filter))
        } else {
            localStorage.setItem("search_without_filter", "true")
        }

        const url= `/reports/asset-actives`;
        navigate(url, true)
    }


    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clearFilter);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Ativos</Template.Title>
                <div className={"flex gap-2"}>
                    <Button target={"success"} onClick={exportData}>
                        <FaFileExcel/><span className="font-bold">Exportar</span>
                    </Button>
                    <Button title="Imprimir" onClick={openPrint}>
                        <FaPrint/>
                    </Button>
                    <Button onClick={newAsset}>
                        <FaPlus /> Adicionar ativo
                    </Button>
                </div>

            </Template.Header>

            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-6">
                                <InputForm id="description" value={state.description} onChange={onChange}
                                           label="Descrição"/>
                            </div>
                            <div className="col-span-2">
                                <InputForm id="asset_number" value={state.asset_number} onChange={onChange}
                                           label="Patrimônio"/>
                            </div>
                            <div className="col-span-2">
                                <InputForm id="serial_number" value={state.serial_number} onChange={onChange}
                                           label="Série"/>
                            </div>
                            <div className="col-span-2">
                                <InputForm id="place" value={state.place} onChange={onChange}
                                           label="Local"/>
                            </div>

                            <div className="col-span-4">
                                <InputForm id="brand" value={state.brand} onChange={onChange}
                                           label="Marca"/>
                            </div>
                            <div className="col-span-4">
                                <InputForm id="model" value={state.model} onChange={onChange}
                                           label="Modelo"/>
                            </div>
                            <div className="col-span-2">
                                <SelectComponent
                                    name="status"
                                    value={statusOpt.find(t => t.value === state?.status) ?? ""}
                                    options={statusOpt}
                                    label={"Status"}
                                    onChange={(selected) => handleInputs("status", selected?.value)}
                                />
                            </div>
                            <div className="col-span-2">
                                <SelectComponent
                                    name="type"
                                    value={typeOpt.find(t => t.value === state?.type) ?? ""}
                                    options={typeOpt}
                                    label={"Tipo"}
                                    onChange={(selected) => handleInputs("type", selected?.value)}
                                />
                            </div>

                            <div className="flex items-center col-span-5">
                                <div className="relative flex-1">
                                    <InputDateTime
                                        id={"warranty"}
                                        selected={state.warranty}
                                        label={"Garantia entre"}
                                        onChange={(date) => handleDate(date, "warranty")}
                                        customIcon
                                        classNameWrapper={"right-date-input"}
                                        dateFormat={"L"}
                                    />
                                </div>

                                <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                                <div className="relative flex-1">
                                    <InputDateTime
                                        id={"warrantyEnd"}
                                        selected={state.warrantyEnd}
                                        label={""}
                                        onChange={(date) => handleDate(date, "warrantyEnd")}
                                        classNameWrapper={"left-date-input"}
                                        dateFormat={"L"}
                                    />
                                </div>
                            </div>

                            <div className="flex col-span-7 gap-4">
                                <InputForm classNameWrapper={"w-full"} id="supplier" value={state.supplier} onChange={onChange}
                                           label="Fornecedor"/>
                                <div className={"flex gap-2"}>
                                    <Button title="Limpar" onClick={() => clearFilter()} target={"primary-outline"}>
                                        <FaEraser/>
                                    </Button>
                                    <Button title="Buscar" onClick={() => apply()}>
                                        <FaSearch/>
                                    </Button>
                                </div>
                            </div>
                        </div>

                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body className="flex">
                        <div className="h-[calc(100vh-249px)] w-full">
                            <AgGridReact
                                ref={gridRef}
                                rowData={asset}
                                columnDefs={columnDefs}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus
                                onCellClicked={onCellClicked}
                                gridOptions={{ localeText: aggridUtils.localization() }}
                                theme={theme}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default AssetActivesList;